import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { Button, Badge, ButtonGroup, Form, ListGroup, Alert, Modal } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { getJobs, createJob, updateJob, fetchStaff } from '../../api/jobs';
import SettingsContext from '../../context/SettingsContext';
import { calculatePrices } from '../../utils/priceCalculator';
import AdjustmentInput from '../../components/job/AdjustmentInput';
import { MenuTiles, OptionTiles, confirmUnpriced } from '../../components/job/JobTiles';
import useAdjustment from '../../hooks/useAdjustment';
import dayjs from 'dayjs';
import { FaPlus, FaArrowRight, FaList, FaTimes, FaExchangeAlt } from 'react-icons/fa';
// InputGroup removed — lock buttons now use absolute positioning
import localeData from 'dayjs/plugin/localeData';
import isBetween from 'dayjs/plugin/isBetween';
import './WorkboardPage.css';

dayjs.extend(localeData);
dayjs.extend(isBetween);

const getDefaultForm = () => ({
    customer_id: null, vehicle_id: null,
    customerName: '', phoneNumber: '', plateNumber: '',
    service_menu_id: '', staffId: '', bayNumber: 1,
    memo: '', status: 'confirmed',
    optionIds: [], depositAmount: 0,
    temp_manufacturer: '', temp_model: '', temp_size_id: '',
    date: dayjs().format('YYYY-MM-DD'),
    time: dayjs().add(1, 'hour').startOf('hour').format('HH:mm'),
    duration: 60
});

const classifyInput = (input) => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    if (/^[a-zA-Z가-힣\s]+$/.test(trimmed)) return 'name';
    if (trimmed.replace(/\D/g, '').length >= 10) return 'phone';
    return 'plate';
};

const WorkboardPage = () => {
    const { settings } = useContext(SettingsContext);
    const [searchParams, setSearchParams] = useSearchParams();

    // ── Calendar State ──
    const viewParam = searchParams.get('view') || 'week';
    const dateParam = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
    const [events, setEvents] = useState([]);
    const [view, setView] = useState(viewParam);
    const [currentDate, setCurrentDate] = useState(dayjs(dateParam));
    const [showCanceled, setShowCanceled] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [dayDetailDate, setDayDetailDate] = useState(null);

    // ── UI Mode ──
    const [workMode, setWorkMode] = useState('idle'); // idle | new | edit
    // 작업 패널을 왼쪽에 둘지. 드래그 좌표는 매 이벤트마다 getBoundingClientRect 로 다시 읽고
    // sticky 보정 JS 는 높이만 보므로, 실시간으로 뒤집어도 계산이 틀어지지 않는다.
    const [panelLeft, setPanelLeft] = useState(() => {
        try { return localStorage.getItem('wb_panel_left') === '1'; } catch (e) { return false; }
    });
    const togglePanelSide = () => setPanelLeft(prev => {
        const next = !prev;
        try { localStorage.setItem('wb_panel_left', next ? '1' : '0'); } catch (e) { /* 사생활 모드 등 */ }
        return next;
    });
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [topPanelOpen, setTopPanelOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');

    // ── Form State ──
    const [formData, setForm] = useState(getDefaultForm());
    const [menus, setMenus] = useState([]);
    const [options, setOptions] = useState([]);
    const [prices, setPrices] = useState({ servicePrices: [], optionPrices: [] });
    const [staffList, setStaffList] = useState([]);
    const [carModels, setCarModels] = useState([]);
    const [vehicleSizes, setVehicleSizes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [fieldSuggestions, setFieldSuggestions] = useState({ field: null, items: [] });
    const [customerHistory, setCustomerHistory] = useState(null);
    const [missingFields, setMissingFields] = useState([]);
    // 지목한 고객에게 번호가 있었는지 — 없을 때만 연락처 칸을 열어 백필을 허용한다.
    //   (서버 routes/jobs.js backfillCustomerPhone 이 고객 마스터에 채운다)
    const [linkedHasPhone, setLinkedHasPhone] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('카드');
    const adjustment = useAdjustment();

    // ── Drag & Resize ──
    const [activeAction, setActiveAction] = useState(null);
    const [activeEventId, setActiveEventId] = useState(null);
    const [previewStart, setPreviewStart] = useState(null);
    const [previewEnd, setPreviewEnd] = useState(null);
    const [previewBay, setPreviewBay] = useState(null);
    const isMovingRef = useRef(false);
    const gridRef = useRef(null);
    const scrollContainerRef = useRef(null);

    // ── New Reservation Preview (click-drag on grid) ──
    const [newPreview, setNewPreview] = useState(null); // { start, end, bay }

    // ── Calendar Constants ──
    const START_HOUR = parseInt(settings?.work_start_time?.split(':')[0]) || 9;
    const END_HOUR = parseInt(settings?.work_end_time?.split(':')[0]) || 22;
    const TOTAL_HOURS = END_HOUR - START_HOUR;
    const PIXELS_PER_HOUR = 120;
    const bayCount = settings?.bay_count || 1;

    // 스케줄러 최소 폭 — 헤더+바디 직접 설정
    useEffect(() => {
        const days = view === 'day' ? 1 : view === 'week' ? 7 : 1;
        const bayColW = window.innerWidth < 992 ? 90 : 60;
        const minW = `${56 + (days * bayCount * bayColW)}px`;
        document.querySelectorAll('.scheduler-header, .scheduler-body').forEach(el => {
            el.style.minWidth = minW;
        });
    }, [view, bayCount]);

    // ── Derived ──
    const resStatus = selectedReservation?.status;

    const filteredEvents = useMemo(() => {
        if (showCanceled) return events;
        return events.filter(ev => ev.status !== 'canceled' && ev.status !== 'noshow');
    }, [events, showCanceled]);

    const visibleDays = useMemo(() => {
        if (view === 'day') return [currentDate.startOf('day')];
        if (view === 'week') {
            const s = currentDate.startOf('week');
            return Array.from({ length: 7 }).map((_, i) => s.add(i, 'day'));
        }
        const startOfGrid = currentDate.startOf('month').startOf('week');
        const endOfGrid = currentDate.endOf('month').endOf('week');
        const days = [];
        let c = startOfGrid;
        while (c.isBefore(endOfGrid)) { days.push(c); c = c.add(1, 'day'); }
        return days;
    }, [currentDate, view]);

    const manufacturers = useMemo(() => {
        // API가 displayOrder 순으로 반환 → 등장 순서 유지
        const seen = new Set();
        const list = [];
        carModels.forEach(m => {
            if (m.manufacturer && !seen.has(m.manufacturer)) {
                seen.add(m.manufacturer);
                list.push(m.manufacturer);
            }
        });
        return list;
    }, [carModels]);
    const filteredModels = carModels.filter(m => m.manufacturer === formData.temp_manufacturer);

    const { totalPrice } = useMemo(() => {
        const sizeId = formData.temp_size_id;
        if (!sizeId || !formData.service_menu_id) return { totalPrice: 0 };
        const result = calculatePrices({
            selectedMenu: menus.find(m => m.id === parseInt(formData.service_menu_id)),
            selectedOptions: options.filter(o => formData.optionIds.includes(o.id)),
            prices, sizeId, adjustmentAmount: adjustment.signed
        });
        return { totalPrice: result.totalPrice };
    }, [formData, menus, options, prices, adjustment.signed]);

    // ══════════════════════════════════════
    //  Effects
    // ══════════════════════════════════════

    useEffect(() => {
        const v = searchParams.get('view') || 'week';
        const d = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
        setView(v); setCurrentDate(dayjs(d));
    }, [searchParams]);

    const fetchEvents = useCallback(async () => {
        const start = visibleDays[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
        const end = visibleDays[visibleDays.length - 1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
        try { const data = await getJobs(start, end); setEvents(data); return data; } catch (err) { console.error(err); return null; }
    }, [visibleDays]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    // 양방향 sticky: 스크롤 내리면 하단 보이고, 올리면 상단 보임
    useEffect(() => {
        const el = document.querySelector('.wb-right');
        if (!el) return;
        const gap = 8; // GNB 바로 아래 최소 간격만
        let lastY = window.scrollY;
        let curTop = gap;

        const calc = () => {
            const ph = el.offsetHeight;
            const vh = window.innerHeight;
            const minTop = vh - ph - 8; // 하단 맞춤
            const maxTop = gap;          // 상단 맞춤
            if (ph <= vh - gap - 8) { el.style.top = `${gap}px`; return; }

            const y = window.scrollY;
            const delta = y - lastY;
            lastY = y;
            curTop = Math.max(minTop, Math.min(maxTop, curTop - delta));
            el.style.top = `${curTop}px`;
        };

        // 초기: 하단 버튼이 보이게
        const ph = el.offsetHeight;
        const vh = window.innerHeight;
        if (ph > vh - gap - 8) curTop = vh - ph - 8;
        el.style.top = `${curTop}px`;

        window.addEventListener('scroll', calc, { passive: true });
        window.addEventListener('resize', calc);
        const obs = new ResizeObserver(() => {
            const ph2 = el.offsetHeight;
            const vh2 = window.innerHeight;
            const minTop = vh2 - ph2 - 8;
            if (ph2 <= vh2 - gap - 8) { el.style.top = `${gap}px`; }
            else { curTop = Math.max(minTop, Math.min(gap, curTop)); el.style.top = `${curTop}px`; }
        });
        obs.observe(el);
        return () => { window.removeEventListener('scroll', calc); window.removeEventListener('resize', calc); obs.disconnect(); };
    }, [workMode, topPanelOpen]);


    useEffect(() => {
        Promise.all([
            api.get('/servicemenus'), api.get('/options'), api.get('/prices'),
            fetchStaff(), api.get('/carmodels'), api.get('/vehiclesizes')
        ]).then(([mR, oR, pR, sR, cmR, vsR]) => {
            setMenus(mR.data.data.filter(m => m.isActive));
            setOptions(oR.data.data.filter(o => o.isActive));
            setPrices(pR.data.data);
            setStaffList(sR);
            setCarModels(cmR.data.data.filter(m => m.isActive));
            setVehicleSizes(vsR.data.data.sizes || []);
        }).catch(err => console.error("Data load failed", err));
    }, []);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const update = () => {
            const sbWidth = el.offsetWidth - el.clientWidth;
            el.closest('.custom-scheduler-wrapper')?.style.setProperty('--scrollbar-width', `${sbWidth}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [view]);

    // ══════════════════════════════════════
    //  Helpers
    // ══════════════════════════════════════

    const updateNavigation = (newView, newDate) => {
        setSearchParams({ view: newView || view, date: dayjs(newDate || currentDate).format('YYYY-MM-DD') });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#ECFDF5'; case 'in_progress': return '#EFF6FF';
            case 'noshow': return '#FEE2E2'; case 'canceled': return '#F3F4F6'; default: return '#FFFBEB';
        }
    };
    const getStatusTextColor = (status) => {
        switch (status) {
            case 'completed': return '#065F46'; case 'in_progress': return '#1E40AF';
            case 'noshow': return '#991B1B'; case 'canceled': return '#6B7280'; default: return '#92400E';
        }
    };
    const getStatusAccent = (status) => {
        switch (status) {
            case 'completed': return '#0D9F6E'; case 'in_progress': return '#1B73E8';
            case 'noshow': return '#DC3545'; case 'canceled': return '#ADB5BD'; default: return '#F59E0B';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return '대기'; case 'confirmed': return '확정';
            case 'in_progress': return '진행'; case 'completed': return '완료';
            case 'noshow': return '노쇼'; case 'canceled': return '취소'; default: return '';
        }
    };

    const resetToIdle = () => {
        setWorkMode('idle'); setSelectedReservation(null);
        setTopPanelOpen(false); setForm(getDefaultForm());
        setCustomerHistory(null); setError(''); setMissingFields([]);
        setPaymentMethod('카드'); adjustment.reset(null); setLinkedHasPhone(false);
        setSearchInput(''); setSuggestions([]);
        setFieldSuggestions({ field: null, items: [] });
    };

    const fetchHistory = async (customerId, plateNumber) => {
        try {
            if (customerId) {
                const res = await api.get(`/customers/${customerId}`);
                setCustomerHistory({ type: 'customer', data: res.data.data });
            } else if (plateNumber) {
                const res = await api.get(`/vehicles/${plateNumber}`);
                setCustomerHistory({ type: 'vehicle', data: res.data.data });
            } else { setCustomerHistory(null); }
        } catch (err) {
            if (err.response?.status !== 404) console.error("History fetch failed", err);
            setCustomerHistory(null);
        }
    };

    // ══════════════════════════════════════
    //  Search Handlers
    // ══════════════════════════════════════

    const debouncedSearch = useMemo(() => {
        let tid;
        return (val) => {
            if (tid) clearTimeout(tid);
            const clean = val.trim().replace(/[^0-9a-zA-Z가-힣]/g, '');
            if (clean.length < 2) { setSuggestions([]); return; }
            tid = setTimeout(async () => {
                try {
                    const res = await api.get(`/vehicles/search?term=${encodeURIComponent(clean)}`);
                    setSuggestions(res.data.data);
                } catch (err) { console.error("Search failed", err); }
            }, 300);
        };
    }, []);

    const handleSearchInput = (e) => {
        setSearchInput(e.target.value);
        debouncedSearch(e.target.value);
    };

    const handleSearchSubmit = () => {
        const trimmed = searchInput.trim();
        if (!trimmed) return;
        if (suggestions.length > 0) { handleSelectFound(suggestions[0]); return; }
        const type = classifyInput(trimmed);
        setForm(prev => {
            const ns = { ...prev };
            if (type === 'phone') { ns.phoneNumber = trimmed; }
            else if (type === 'name') { ns.customerName = trimmed; }
            else { ns.plateNumber = trimmed; }
            return ns;
        });
        setTopPanelOpen(true);
        setSuggestions([]);
    };

    const handleSelectFound = (v) => {
        // 검색 결과 선택 시 기존 맵핑/폼을 전부 교체 (X 버튼 효과).
        // 이전 값을 유지(|| prev)하면 "김아무개 01022223333" 같은 혼종 상태가 생김.
        setForm(prev => ({
            ...prev,
            vehicle_id: v.id ?? null,
            customer_id: v.customer_id ?? null,
            plateNumber: v.plateNumber || '',
            customerName: v.customerName || '',
            phoneNumber: v.phoneNumber || '',
            temp_manufacturer: v.manufacturer || '',
            temp_model: v.modelName || '',
            temp_size_id: v.vehicle_size_id || ''
        }));
        setSuggestions([]); setTopPanelOpen(true);
        fetchHistory(v.customer_id, v.plateNumber);
    };

    // ══════════════════════════════════════
    //  Form Handlers
    // ══════════════════════════════════════

    const fieldSearchTimer = useRef(null);

    const handleFormChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));

        // 필드별 자동완성 검색
        if (fieldSearchTimer.current) clearTimeout(fieldSearchTimer.current);
        fieldSearchTimer.current = setTimeout(() => {
            if (field === 'plateNumber' && !formData.vehicle_id && value.length >= 3) {
                api.get(`/vehicles/search?term=${encodeURIComponent(value.trim())}`)
                    .then(res => setFieldSuggestions({ field: 'plateNumber', items: res.data.data || [] }))
                    .catch(() => {});
            } else if (field === 'customerName' && !formData.customer_id && value.trim().length >= 2) {
                api.get(`/customers/search?name=${encodeURIComponent(value.trim())}`)
                    .then(res => setFieldSuggestions({ field: 'customerName', items: res.data.data || [] }))
                    .catch(() => {});
            } else if (field === 'phoneNumber' && !formData.customer_id) {
                const digits = value.replace(/\D/g, '');
                if (digits.length === 4 || digits.length >= 10) {
                    api.get(`/customers/search?phone=${encodeURIComponent(digits)}`)
                        .then(res => setFieldSuggestions({ field: 'phoneNumber', items: res.data.data || [] }))
                        .catch(() => {});
                } else {
                    setFieldSuggestions({ field: null, items: [] });
                }
            } else {
                setFieldSuggestions({ field: null, items: [] });
            }
        }, 300);
    };

    const handleUnmap = (type) => {
        if (type === 'vehicle') {
            setForm(prev => {
                const next = {
                    ...prev,
                    vehicle_id: null, plateNumber: '',
                    temp_manufacturer: '', temp_model: '', temp_size_id: ''
                };
                // 고객 매핑이 남아있으면 고객 기준 CRM 유지
                if (next.customer_id) {
                    fetchHistory(next.customer_id, null);
                } else {
                    setCustomerHistory(null);
                }
                return next;
            });
        } else if (type === 'customer') {
            setForm(prev => {
                const next = {
                    ...prev,
                    customer_id: null, customerName: '', phoneNumber: ''
                };
                setLinkedHasPhone(false);
                // 차량 매핑이 남아있으면 차량 기준 CRM 유지
                if (next.vehicle_id) {
                    fetchHistory(null, prev.plateNumber);
                } else {
                    setCustomerHistory(null);
                }
                return next;
            });
        }
    };

    const handleSelectVehicleSuggestion = (vehicle) => {
        setForm(prev => {
            const ns = { ...prev, vehicle_id: vehicle.id, plateNumber: vehicle.plateNumber,
                temp_manufacturer: vehicle.manufacturer || prev.temp_manufacturer,
                temp_model: vehicle.modelName || prev.temp_model,
                temp_size_id: vehicle.vehicle_size_id || prev.temp_size_id };
            // 차량에 고객이 있으면 덮어씌움 (차량이 강한 식별자)
            if (vehicle.customer_id) {
                ns.customer_id = vehicle.customer_id;
                ns.customerName = vehicle.customerName || '';
                ns.phoneNumber = vehicle.phoneNumber || '';
                setLinkedHasPhone(!!vehicle.phoneNumber);
            }
            return ns;
        });
        setFieldSuggestions({ field: null, items: [] });
        fetchHistory(vehicle.customer_id || formData.customer_id, vehicle.plateNumber);
    };

    const handleSelectCustomerSuggestion = (customer) => {
        setForm(prev => ({ ...prev, customer_id: customer.id,
            customerName: customer.customerName, phoneNumber: customer.phoneNumber || '' }));
        setLinkedHasPhone(!!customer.phoneNumber);
        setFieldSuggestions({ field: null, items: [] });
        fetchHistory(customer.id, formData.plateNumber);
    };

    const updateVehicleDB = (vehicleId, fields) => {
        if (!vehicleId) return;
        api.put(`/vehicles/${vehicleId}`, fields).catch(err => console.error('차량 정보 업데이트 실패', err));
    };

    const handleManufacturerChange = (value) => {
        setForm(prev => ({ ...prev, temp_manufacturer: value, temp_model: '' }));
        updateVehicleDB(formData.vehicle_id, { carModelId: null });
    };

    const handleModelChange = (value) => {
        setForm(prev => ({ ...prev, temp_model: value }));
        const matched = carModels.find(m => m.manufacturer === formData.temp_manufacturer && m.modelName === value);
        if (matched) updateVehicleDB(formData.vehicle_id, { carModelId: matched.id });
    };

    const handleSizeChange = (value) => {
        setForm(prev => ({ ...prev, temp_size_id: value }));
        updateVehicleDB(formData.vehicle_id, { vehicle_size_id: value || null });
    };

    const applyVehicleFromHistory = (v) => {
        setForm(prev => ({
            ...prev, vehicle_id: v.id, plateNumber: v.plateNumber,
            temp_manufacturer: v.manufacturer || '', temp_model: v.modelName || '',
            temp_size_id: v.vehicle_size_id || prev.temp_size_id
        }));
    };

    // ══════════════════════════════════════
    //  Calendar Interaction
    // ══════════════════════════════════════

    // Job row(ev) → 폼 state 매핑. 백엔드 컬럼명 차이(courseId↔service_menu_id 등) 흡수.
    const mapEventToForm = (ev) => {
        const start = dayjs(ev.startTime);
        const end = dayjs(ev.endTime);
        let opts = ev.optionIds;
        if (typeof opts === 'string') { try { opts = JSON.parse(opts); } catch { opts = []; } }
        return {
            ...ev,
            vehicle_id: ev.vehicleId || ev.vehicle_id || null,
            date: start.format('YYYY-MM-DD'), time: start.format('HH:mm'),
            duration: end.diff(start, 'minute'),
            optionIds: Array.isArray(opts) ? opts : [],
            staffId: ev.staffId || '',
            bayNumber: ev.bayNumber || 1,
            temp_manufacturer: ev.temp_manufacturer || ev.manufacturer || '',
            temp_model: ev.temp_model || ev.modelName || '',
            temp_size_id: ev.temp_size_id || '',
            service_menu_id: ev.service_menu_id || ev.courseId || '',
            memo: ev.memo || '', plateNumber: ev.plateNumber || '',
            customerName: ev.linkedCustomerName || ev.customerName || '',
            phoneNumber: ev.phoneNumber || ''
        };
    };

    const handleEventClick = (ev) => {
        // 신규 예약 작성 중 + 핵심 정보 입력돼있으면 확인
        if (workMode === 'new' && (formData.plateNumber || formData.phoneNumber || formData.customerName)) {
            if (!window.confirm('작성 중인 내용이 있습니다. 다른 예약으로 이동하시겠습니까?')) return;
        }
        setSelectedReservation(ev); setWorkMode('edit');
        setForm(mapEventToForm(ev));
        setTopPanelOpen(true);
        fetchHistory(ev.customer_id, ev.plateNumber);
        setError(''); setMissingFields([]);
        // 입고 정산 prefill: paymentMethod는 컬럼, 조정금은 priceBreakdown JSON (훅이 파싱)
        setPaymentMethod(ev.paymentMethod || '카드');
        adjustment.reset(ev);
        setLinkedHasPhone(!!ev.phoneNumber);
    };

    const calcGridPosition = (clientX, clientY) => {
        if (!gridRef.current) return null;
        const rect = gridRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width - 1));
        const y = Math.max(0, Math.min(clientY - rect.top, TOTAL_HOURS * PIXELS_PER_HOUR));
        const dayWidth = rect.width / (view === 'week' ? 7 : 1);
        const dayIndex = Math.floor(x / dayWidth);
        const bayWidth = dayWidth / bayCount;
        const bay = Math.floor((x % dayWidth) / bayWidth) + 1;
        const snap30 = Math.floor((y / PIXELS_PER_HOUR) * 60 / 30) * 30;
        const time = visibleDays[dayIndex].hour(START_HOUR).minute(0).add(snap30, 'minute');
        return { time, bay, dayIndex };
    };

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 992;

    const openNewForm = (time, bay, duration = 30) => {
        if (workMode === 'edit') {
            // ① 예약 수정 중 → 빈 셀: 새 예약으로 리셋 (CRM 포함)
            setSelectedReservation(null); setWorkMode('new');
            setForm({
                ...getDefaultForm(),
                date: time.format('YYYY-MM-DD'), time: time.format('HH:mm'),
                bayNumber: bay, duration, status: 'confirmed'
            });
            setCustomerHistory(null);
            setSearchInput('');
        } else if (topPanelOpen) {
            // ② 폼이 이미 열려있음 (new 또는 검색 진입 idle): 시간/베이만 변경, 입력값 유지
            setForm(prev => ({
                ...prev,
                date: time.format('YYYY-MM-DD'), time: time.format('HH:mm'),
                bayNumber: bay, duration
            }));
            if (workMode !== 'new') setWorkMode('new');   // 검색 진입('idle')도 new로 승격
        } else {
            // ③ 폼 닫혀있던 상태 → 새로 열기
            setSelectedReservation(null); setWorkMode('new');
            setForm({
                ...getDefaultForm(),
                date: time.format('YYYY-MM-DD'), time: time.format('HH:mm'),
                bayNumber: bay, duration, status: 'confirmed'
            });
            setTopPanelOpen(true);
            setSearchInput('');
        }
        setError(''); setMissingFields([]);
    };

    const handleGridMouseDown = (e) => {
        if (e.target.closest('.event-card') || e.target.closest('.ev-time-label') || e.target.closest('.new-reservation-preview') || activeAction || e.button !== 0) return;
        const pos = calcGridPosition(e.clientX, e.clientY);
        if (!pos) return;

        // 모바일: 탭으로 바로 폼 열기 (드래그 없이)
        if (isMobile) {
            openNewForm(pos.time, pos.bay);
            return;
        }

        const dragStart = pos.time;
        const dragBay = pos.bay;
        let finalStart = dragStart;
        let finalEnd = dragStart.add(30, 'minute');

        setNewPreview({ start: finalStart, end: finalEnd, bay: dragBay });

        const onMove = (me) => {
            const cur = calcGridPosition(me.clientX, me.clientY);
            if (!cur) return;
            if (!cur.time.isSame(dragStart, 'day')) return;
            const curTime = cur.time.add(30, 'minute');
            if (curTime.isAfter(dragStart)) {
                finalStart = dragStart;
                finalEnd = curTime;
            } else {
                finalStart = cur.time;
                finalEnd = dragStart.add(30, 'minute');
            }
            setNewPreview({ start: finalStart, end: finalEnd, bay: dragBay });
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            const duration = Math.max(30, finalEnd.diff(finalStart, 'minute'));
            setNewPreview(null);
            openNewForm(finalStart, dragBay, duration);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const handleNewFromDate = (day) => {
        setSelectedReservation(null); setWorkMode('new');
        setForm(prev => ({
            ...prev,
            date: day.format('YYYY-MM-DD'), time: '10:00',
            bayNumber: prev.bayNumber || 1, duration: prev.duration || 60, status: 'confirmed'
        }));
        setError(''); setMissingFields([]);
    };

    // ── Drag & Resize (from ReservationsPage) ──

    const finalizeAction = useCallback(async (eventId, start, end, bay) => {
        const orig = events.find(e => e.id === eventId);
        const oldStart = dayjs(orig?.startTime).format('HH:mm');
        const oldEnd = dayjs(orig?.endTime).format('HH:mm');
        const newStart = start.format('HH:mm');
        const newEnd = end.format('HH:mm');
        const oldBay = orig?.bayNumber;

        let msg = `${oldStart}~${oldEnd} → ${newStart}~${newEnd}`;
        if (oldBay !== bay) msg += ` (${oldBay}번→${bay}번 베이)`;
        if (!window.confirm(msg + '\n변경하시겠습니까?')) {
            fetchEvents();
            return;
        }

        try {
            await updateJob(eventId, {
                startTime: start.format('YYYY-MM-DD HH:mm:ss'),
                endTime: end.format('YYYY-MM-DD HH:mm:ss'),
                bayNumber: bay
            });
            const refreshed = await fetchEvents();
            // 드래그 완료 후 해당 이벤트를 자동 선택하여 시간 라벨 유지
            if (refreshed) {
                const updated = refreshed.find(e => e.id === eventId);
                if (updated) {
                    setSelectedReservation(updated);
                    setWorkMode('edit');
                    setForm(mapEventToForm(updated));
                }
            }
        } catch (err) { alert(err.response?.data?.error || '변경 실패'); fetchEvents(); }
    }, [fetchEvents, events]);

    const handleTimeLabelDrag = (e, event, edge) => {
        e.stopPropagation(); e.preventDefault();
        setActiveAction('resize'); setActiveEventId(event.id);
        let cStart = dayjs(event.startTime), cEnd = dayjs(event.endTime);
        const cBay = event.bayNumber;
        isMovingRef.current = false;

        const getTimeFromY = (clientY) => {
            if (!gridRef.current) return null;
            const gr = gridRef.current.getBoundingClientRect();
            // scrollContainerRef의 스크롤 오프셋 반영
            const scrollEl = scrollContainerRef.current;
            const scrollTop = scrollEl ? scrollEl.scrollTop : 0;
            const containerTop = scrollEl ? scrollEl.getBoundingClientRect().top : gr.top;
            const y = Math.max(0, Math.min(clientY - containerTop + scrollTop, TOTAL_HOURS * PIXELS_PER_HOUR));
            const snap = Math.round((y / PIXELS_PER_HOUR * 60) / 30) * 30;
            return dayjs(event.startTime).startOf('day').hour(START_HOUR).add(snap, 'minute');
        };

        const onMove = (me) => {
            const clientY = me.touches ? me.touches[0].clientY : me.clientY;
            const nt = getTimeFromY(clientY);
            if (!nt) return;
            isMovingRef.current = true;
            if (edge === 'end') {
                const minE = cStart.add(30, 'minute');
                cEnd = nt.isBefore(minE) ? minE : nt;
            } else {
                const maxS = cEnd.subtract(30, 'minute');
                cStart = nt.isAfter(maxS) ? maxS : nt;
            }
            setPreviewStart(cStart); setPreviewEnd(cEnd); setPreviewBay(cBay);
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onUp);
            if (isMovingRef.current) finalizeAction(event.id, cStart, cEnd, cBay);
            setActiveAction(null); setActiveEventId(null);
            setPreviewStart(null); setPreviewEnd(null); setPreviewBay(null);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onUp);
    };

    const handleFormPreviewResize = (e, edge) => {
        e.stopPropagation(); e.preventDefault();
        const fStart = dayjs(`${formData.date} ${formData.time}`);
        const fEnd = fStart.add(formData.duration || 30, 'minute');

        const getTimeFromY = (clientY) => {
            if (!gridRef.current) return null;
            const scrollEl = scrollContainerRef.current;
            const scrollTop = scrollEl ? scrollEl.scrollTop : 0;
            const containerTop = scrollEl ? scrollEl.getBoundingClientRect().top : gridRef.current.getBoundingClientRect().top;
            const y = Math.max(0, Math.min(clientY - containerTop + scrollTop, TOTAL_HOURS * PIXELS_PER_HOUR));
            const snap = Math.round((y / PIXELS_PER_HOUR * 60) / 30) * 30;
            return fStart.startOf('day').hour(START_HOUR).add(snap, 'minute');
        };

        const onMove = (me) => {
            const clientY = me.touches ? me.touches[0].clientY : me.clientY;
            const nt = getTimeFromY(clientY);
            if (!nt) return;
            if (edge === 'end') {
                const minEnd = fStart.add(30, 'minute');
                const newEnd = nt.isBefore(minEnd) ? minEnd : nt;
                setForm(prev => ({ ...prev, duration: newEnd.diff(dayjs(`${prev.date} ${prev.time}`), 'minute') }));
            } else {
                const maxStart = fEnd.subtract(30, 'minute');
                const newStart = nt.isAfter(maxStart) ? maxStart : nt;
                const newDuration = fEnd.diff(newStart, 'minute');
                setForm(prev => ({ ...prev, time: newStart.format('HH:mm'), duration: newDuration }));
            }
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onUp);
    };

    const handleActionMouseDown = (e, event) => {
        // 시간 라벨에서 시작된 이벤트는 무시 (handleTimeLabelDrag가 처리)
        if (e.target.closest('.ev-time-label')) return;
        e.stopPropagation(); e.preventDefault();
        if (isMobile) return;
        setActiveAction('drag'); setActiveEventId(event.id);
        let cStart = dayjs(event.startTime), cEnd = dayjs(event.endTime), cBay = event.bayNumber;
        const rect = e.currentTarget.closest('.event-card').getBoundingClientRect();
        const grabOffset = e.clientY - rect.top;
        const startY = e.clientY;
        const startX = e.clientX;
        isMovingRef.current = false;

        const onMove = (me) => {
            if (!gridRef.current) return;
            if (Math.abs(me.clientY - startY) > 3 || Math.abs(me.clientX - startX) > 3) isMovingRef.current = true;
            const gr = gridRef.current.getBoundingClientRect();
            const dw = gr.width / (view === 'week' ? 7 : 1);
            const bw = dw / bayCount;

            const x = Math.max(0, Math.min(me.clientX - gr.left, gr.width - 1));
            const di = Math.floor(x / dw);
            cBay = Math.floor((x % dw) / bw) + 1;
            const nd = visibleDays[di];
            const mins = Math.round(((me.clientY - gr.top - grabOffset) / PIXELS_PER_HOUR * 60) / 30) * 30;
            const dur = dayjs(event.endTime).diff(dayjs(event.startTime), 'minute');
            cStart = nd.hour(START_HOUR).minute(0).second(0).add(mins, 'minute');
            const sLim = nd.hour(START_HOUR).minute(0);
            const eLim = nd.hour(END_HOUR).minute(0);
            if (cStart.isBefore(sLim)) cStart = sLim;
            if (cStart.add(dur, 'minute').isAfter(eLim)) cStart = eLim.subtract(dur, 'minute');
            cEnd = cStart.add(dur, 'minute');

            setPreviewStart(cStart); setPreviewEnd(cEnd); setPreviewBay(cBay);
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            if (isMovingRef.current) finalizeAction(event.id, cStart, cEnd, cBay);
            setActiveAction(null); setActiveEventId(null);
            setPreviewStart(null); setPreviewEnd(null); setPreviewBay(null);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const getEventStyles = (event) => {
        let start = dayjs(event.startTime), end = dayjs(event.endTime), bay = event.bayNumber;
        if (activeEventId === event.id) {
            if (previewStart) start = previewStart;
            if (previewEnd) end = previewEnd;
            if (previewBay) bay = previewBay;
        }
        // 수정 모드: 폼에서 시간/베이 변경 시 실시간 반영 (드래그/리사이즈 중에는 preview 우선)
        if (selectedReservation?.id === event.id && workMode === 'edit' && formData.date && formData.time && activeEventId !== event.id) {
            const formStart = dayjs(`${formData.date} ${formData.time}`);
            const formEnd = formStart.add(formData.duration || 30, 'minute');
            if (formStart.isValid()) {
                start = formStart;
                end = formEnd;
            }
            if (formData.bay) bay = formData.bay;
        }
        const dayIndex = visibleDays.findIndex(d => d.isSame(start, 'day'));
        if (dayIndex === -1) return { display: 'none' };
        const top = (start.diff(start.startOf('day').hour(START_HOUR), 'minute') / 60) * PIXELS_PER_HOUR;
        const height = (end.diff(start, 'minute') / 60) * PIXELS_PER_HOUR;
        const wp = 100 / (visibleDays.length * bayCount);
        const lp = ((dayIndex * bayCount) + (bay - 1)) * wp;
        return { top: `${top}px`, height: `${height}px`, left: `${lp}%`, width: `${wp}%`, position: 'absolute', zIndex: activeEventId === event.id ? 100 : 10 };
    };

    // ══════════════════════════════════════
    //  Save & Action Handlers
    // ══════════════════════════════════════

    const handleSaveSuccess = () => { fetchEvents(); resetToIdle(); };

    const handleSubmit = async () => {
        // 입고 상태에서 서비스 내용 변경 시 확인 (완료는 immutable이라 잠긴 필드는 아래에서 제거됨)
        if (selectedReservation && resStatus === 'in_progress') {
            const menuChanged = String(formData.service_menu_id) !== String(selectedReservation.service_menu_id || selectedReservation.courseId || '');
            const optsOld = JSON.stringify((selectedReservation.optionIds || []).sort());
            const optsNew = JSON.stringify((formData.optionIds || []).sort());
            const optionsChanged = optsOld !== optsNew;
            if (menuChanged || optionsChanged) {
                if (!window.confirm('서비스 내용 및 금액이 변경됩니다. 계속하시겠습니까?')) return;
            }
        }

        setLoading(true); setError('');
        const startStr = `${formData.date} ${formData.time}`;
        let sd = {
            plateNumber: formData.plateNumber, customerName: formData.customerName || '',
            phoneNumber: formData.phoneNumber || '', memo: formData.memo || '',
            service_menu_id: formData.service_menu_id,
            courseId: formData.service_menu_id ? parseInt(formData.service_menu_id) : undefined,
            optionIds: formData.optionIds || [],
            bayNumber: parseInt(formData.bayNumber) || 1,
            staffId: formData.staffId === '' ? null : formData.staffId,
            startTime: dayjs(startStr).format('YYYY-MM-DD HH:mm:ss'),
            endTime: dayjs(startStr).add(formData.duration, 'minute').format('YYYY-MM-DD HH:mm:ss'),
            temp_size_id: formData.temp_size_id, temp_manufacturer: formData.temp_manufacturer,
            temp_model: formData.temp_model, customer_id: formData.customer_id, vehicle_id: formData.vehicle_id,
            status: formData.status,
            finalPrice: totalPrice,
            paymentMethod,
            adjustmentAmount: adjustment.signed,
        };
        if (selectedReservation) delete sd.status;
        try {
            if (selectedReservation) await updateJob(selectedReservation.id, sd);
            else await createJob(sd);
            handleSaveSuccess();
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || err.response?.data?.error || '저장 실패');
        } finally { setLoading(false); }
    };

    const handleStartWork = async () => {
        const missing = [];
        if (!formData.plateNumber) missing.push('plateNumber');
        if (settings?.is_car_model_required && !formData.temp_manufacturer) missing.push('temp_manufacturer');
        if (settings?.is_car_model_required && !formData.temp_model) missing.push('temp_model');
        if (!formData.temp_size_id) missing.push('temp_size_id');
        if (!formData.service_menu_id) missing.push('service_menu_id');
        if (missing.length > 0) { setMissingFields(missing); setError(settings?.is_car_model_required ? '차량번호, 차종(제조사/모델), 사이즈, 메뉴를 모두 입력해주세요.' : '차량번호, 사이즈, 메뉴를 모두 입력해주세요.'); return; }
        // 가격표에 없는 조합이면 0원으로 계산된다는 걸 알린다 (차단 아님)
        if (!confirmUnpriced({ menus, options, prices, sizeId: formData.temp_size_id,
            menuId: formData.service_menu_id, optionIds: formData.optionIds || [] })) return;

        setLoading(true); setError('');
        try {
            let curVId = formData.vehicle_id, curRId = selectedReservation?.id;
            const useReservation = !!settings?.use_reservation;

            if (useReservation && (!curVId || !curRId)) {
                const startStr = `${formData.date} ${formData.time}`;
                const sd = {
                    plateNumber: formData.plateNumber, customerName: formData.customerName || '',
                    phoneNumber: formData.phoneNumber || '', memo: formData.memo || '',
                    service_menu_id: formData.service_menu_id,
                    courseId: formData.service_menu_id ? parseInt(formData.service_menu_id) : undefined,
                    optionIds: formData.optionIds || [],
                    bayNumber: parseInt(formData.bayNumber) || 1,
                    staffId: formData.staffId === '' ? null : formData.staffId,
                    startTime: dayjs(startStr).format('YYYY-MM-DD HH:mm:ss'),
                    endTime: dayjs(startStr).add(formData.duration, 'minute').format('YYYY-MM-DD HH:mm:ss'),
                    temp_size_id: formData.temp_size_id, temp_manufacturer: formData.temp_manufacturer,
                    temp_model: formData.temp_model, customer_id: formData.customer_id, vehicle_id: formData.vehicle_id
                };
                const saved = selectedReservation
                    ? await updateJob(selectedReservation.id, sd)
                    : await createJob(sd);
                curVId = saved?.vehicle_id || curVId;
                curRId = saved?.id || curRId;
                if (!curVId) throw new Error("차량 정보를 생성할 수 없습니다.");
            }
            await updateJob(curRId, {
                status: 'in_progress',
                vehicleId: curVId, courseId: parseInt(formData.service_menu_id),
                optionIds: formData.optionIds || [],
                finalPrice: totalPrice,
                comment: formData.memo || '', paymentMethod,
                adjustmentAmount: adjustment.signed, adjustmentReason: adjustment.reason,
                customerName: formData.customerName || '', phoneNumber: formData.phoneNumber || '',
                // 입고 시 폼에 입력한 차종/사이즈를 차량에 반영 (예약 시 미정이었던 경우 여기서 채움)
                plateNumber: formData.plateNumber,
                temp_manufacturer: formData.temp_manufacturer,
                temp_model: formData.temp_model,
                temp_size_id: formData.temp_size_id,
            });
            handleSaveSuccess();
        } catch (err) {
            setError(err.response?.data?.error || err.message || '입고 처리 실패');
        } finally { setLoading(false); }
    };

    const handleComplete = async () => {
        const missing = [];
        if (!formData.plateNumber) missing.push('차량번호');
        if (settings?.is_car_model_required && !formData.temp_manufacturer) missing.push('제조사');
        if (settings?.is_car_model_required && !formData.temp_model) missing.push('모델');
        if (!formData.temp_size_id) missing.push('사이즈');
        if (!formData.service_menu_id) missing.push('메뉴');
        if (missing.length > 0) {
            const fieldMap = { '차량번호': 'plateNumber', '제조사': 'temp_manufacturer', '모델': 'temp_model', '사이즈': 'temp_size_id', '메뉴': 'service_menu_id' };
            setMissingFields(missing.map(m => fieldMap[m] || m));
            setError(`[${missing.join(', ')}] 정보가 누락되었습니다.`); return;
        }
        // 가격표에 없는 조합이면 0원으로 계산된다는 걸 알린다 (차단 아님)
        if (!confirmUnpriced({ menus, options, prices, sizeId: formData.temp_size_id,
            menuId: formData.service_menu_id, optionIds: formData.optionIds || [] })) return;

        const menuChanged = selectedReservation && String(formData.service_menu_id) !== String(selectedReservation.service_menu_id || selectedReservation.courseId || '');
        const optsOld = JSON.stringify((selectedReservation?.optionIds || []).slice().sort());
        const optsNew = JSON.stringify((formData.optionIds || []).slice().sort());
        const optionsChanged = optsOld !== optsNew;
        const confirmMsg = (menuChanged || optionsChanged)
            ? '서비스 내용/금액 변경 사항이 함께 저장됩니다. 완료 처리하시겠습니까?'
            : '작업을 완료 처리하시겠습니까?';
        if (!window.confirm(confirmMsg)) return;
        setLoading(true); setError('');
        try {
            const startStr = `${formData.date} ${formData.time}`;
            const now = dayjs();
            const plannedEnd = dayjs(startStr).add(formData.duration, 'minute');
            let endTime = plannedEnd.format('YYYY-MM-DD HH:mm:ss');
            if (now.isBefore(plannedEnd)) {
                const nd = Math.max(10, now.diff(dayjs(startStr), 'minute'));
                endTime = dayjs(startStr).add(nd, 'minute').format('YYYY-MM-DD HH:mm:ss');
            }
            await updateJob(selectedReservation.id, {
                status: 'completed',
                courseId: parseInt(formData.service_menu_id),
                optionIds: formData.optionIds || [],
                finalPrice: totalPrice,
                comment: formData.memo || '',
                paymentMethod,
                adjustmentAmount: adjustment.signed,
                adjustmentReason: adjustment.reason,
                customerName: formData.customerName || '',
                phoneNumber: formData.phoneNumber || '',
                endTime,
                // 완료 시 폼에 입력한 차종/사이즈를 차량에 반영 (입고 생략 후 바로 완료한 경우 대비)
                plateNumber: formData.plateNumber,
                temp_manufacturer: formData.temp_manufacturer,
                temp_model: formData.temp_model,
                temp_size_id: formData.temp_size_id,
            });
            handleSaveSuccess();
        } catch (err) { setError(err.response?.data?.error || err.message || '완료 처리 실패'); }
        finally { setLoading(false); }
    };

    const handleCancelWork = async () => {
        if (!window.confirm('작업을 취소하시겠습니까?')) return;
        setLoading(true);
        try {
            await updateJob(selectedReservation.id, { status: 'confirmed' });
            handleSaveSuccess();
        } catch (err) { setError(err.response?.data?.error || '작업 취소 실패'); }
        finally { setLoading(false); }
    };

    const handleMarkNoshow = async () => {
        if (!window.confirm('노쇼 처리하시겠습니까?')) return;
        setLoading(true);
        try { await updateJob(selectedReservation.id, { status: 'noshow' }); handleSaveSuccess(); }
        catch (err) { setError(err.response?.data?.error || '노쇼 처리 실패'); }
        finally { setLoading(false); }
    };

    const handleCancelReservation = async () => {
        if (!window.confirm('예약을 취소하시겠습니까?')) return;
        setLoading(true);
        try { await updateJob(selectedReservation.id, { status: 'canceled' }); handleSaveSuccess(); }
        catch (err) { setError(err.response?.data?.error || '예약 취소 실패'); }
        finally { setLoading(false); }
    };

    // ══════════════════════════════════════
    //  Render: Month View
    // ══════════════════════════════════════

    const MAX_VISIBLE_EVENTS = 3;

    const renderMonthView = () => {
        const weekRows = [];
        for (let i = 0; i < visibleDays.length; i += 7) weekRows.push(visibleDays.slice(i, i + 7));
        return (
            <div className="month-grid d-flex flex-column flex-grow-1 bg-white" style={{ overflow: 'auto' }}>
                <div className="month-header d-flex border-bottom bg-light sticky-top" style={{ zIndex: 5 }}>
                    {['일','월','화','수','목','금','토'].map(d => (
                        <div key={d} className="month-header-cell text-center py-2 fw-bold small text-muted border-end">{d}</div>
                    ))}
                </div>
                {weekRows.map((week, i) => (
                    <div key={i} className="month-week-row d-flex">
                        {week.map((day, j) => {
                            const isCur = day.isSame(currentDate, 'month');
                            const de = filteredEvents.filter(ev => dayjs(ev.startTime).isSame(day, 'day'))
                                .sort((a,b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
                            const vis = de.slice(0, MAX_VISIBLE_EVENTS);
                            const over = de.length - MAX_VISIBLE_EVENTS;
                            return (
                                <div key={j} className={`month-day-cell p-2 ${!isCur ? 'bg-disabled opacity-50' : ''}`}
                                    onClick={() => setDayDetailDate(day)}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className={`day-number ${day.isSame(dayjs(), 'day') ? 'today-badge' : ''}`}>{day.date()}</span>
                                        <div className="day-header-actions">
                                            <div className="day-action-btn text-primary" title="예약 추가" onClick={(e) => { e.stopPropagation(); handleNewFromDate(day); }}><FaPlus size={12} /></div>
                                        </div>
                                    </div>
                                    {/* 모바일: 건수 뱃지만 */}
                                    <div className="month-event-count justify-content-center">
                                        {de.length > 0 && <Badge bg="primary" pill className="wb-text-xs">{de.length}</Badge>}
                                    </div>
                                    {/* PC: 이벤트 목록 */}
                                    <div className="month-event-list d-flex flex-column gap-1">
                                        {vis.map(ev => (
                                            <div key={ev.id} className="month-event-strip" style={{ backgroundColor: getStatusColor(ev.status), color: getStatusTextColor(ev.status) }}
                                                onClick={(e) => { e.stopPropagation(); handleEventClick(ev); }}
                                                title={`${dayjs(ev.startTime).format('HH:mm')} ${ev.customerName || ev.plateNumber} | ${ev.staffName || '미지정'}`}>
                                                <span className="status-tag me-1">{getStatusLabel(ev.status)}</span>
                                                <span className="opacity-75 me-1">{dayjs(ev.startTime).format('HH:mm')}</span>
                                                <span className="fw-bold">{ev.customerName || ev.plateNumber || '-'}</span>
                                            </div>
                                        ))}
                                        {over > 0 && (
                                            <div className="month-event-more" onClick={(e) => { e.stopPropagation(); setDayDetailDate(day); }}>+{over}건 더보기</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    // ══════════════════════════════════════
    //  Render: Idle Panel (오늘의 예약)
    // ══════════════════════════════════════

    const [mobileIdleExpanded, setMobileIdleExpanded] = useState(false);

    const renderIdlePanel = () => {
        const today = dayjs();
        const todayEvts = events
            .filter(ev => ev.status !== 'noshow' && ev.status !== 'canceled')
            .filter(ev => dayjs(ev.startTime).isSame(today, 'day'))
            .sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
        const counts = {
            active: todayEvts.filter(r => ['pending', 'confirmed'].includes(r.status)).length,
            inProg: todayEvts.filter(r => r.status === 'in_progress').length,
            done: todayEvts.filter(r => r.status === 'completed').length,
        };
        return (
            <>
                <div className="idle-panel-header">
                    <div className="idle-panel-title">오늘의 일정</div>
                    <Button size="sm" variant="outline-secondary" className="d-md-none wb-text-sm"
                        onClick={() => setMobileIdleExpanded(p => !p)}>
                        {mobileIdleExpanded ? '접기' : '더보기'}
                    </Button>
                </div>
                <div className="idle-stats-row">
                    <div className="idle-stat-chip">
                        <div className="idle-stat-num" style={{ color: '#F59E0B' }}>{counts.active}</div>
                        <div className="idle-stat-label">예약</div>
                    </div>
                    <div className="idle-stat-chip">
                        <div className="idle-stat-num" style={{ color: '#1B73E8' }}>{counts.inProg}</div>
                        <div className="idle-stat-label">작업중</div>
                    </div>
                    <div className="idle-stat-chip">
                        <div className="idle-stat-num" style={{ color: '#0D9F6E' }}>{counts.done}</div>
                        <div className="idle-stat-label">완료</div>
                    </div>
                </div>
                <div className={`work-panel-body ${mobileIdleExpanded ? '' : 'd-none d-md-block'}`}>
                    {todayEvts.length > 0 ? todayEvts.map(ev => (
                        <div key={ev.id} className="idle-list-item"
                            style={{ cursor: 'pointer' }} onClick={() => handleEventClick(ev)}>
                            <div className="idle-time-block">
                                <div className="idle-time-num">{dayjs(ev.startTime).format('HH:mm')}</div>
                                <div className="idle-time-bay">{ev.bayNumber}번</div>
                            </div>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="idle-item-name">{ev.customerName || ev.plateNumber || '-'}</div>
                                <div className="idle-item-detail">{ev.serviceName || '메뉴 미지정'} · {ev.staffName || '미정'}</div>
                            </div>
                            <span className="idle-item-status" style={{ backgroundColor: getStatusColor(ev.status), color: getStatusTextColor(ev.status) }}>
                                {getStatusLabel(ev.status)}
                            </span>
                        </div>
                    )) : <div className="text-center text-muted py-5 small">오늘 등록된 예약이 없습니다.</div>}
                </div>
            </>
        );
    };

    // ══════════════════════════════════════
    //  Render: Work Form
    // ══════════════════════════════════════

    const renderWorkForm = () => {
        const isEdit = workMode === 'edit';
        const showPayment = workMode === 'new' || (isEdit && (resStatus === 'confirmed' || resStatus === 'pending' || resStatus === 'in_progress' || resStatus === 'completed'));
        const oldPm = selectedReservation?.paymentMethod;
        const showPointEditWarning = isEdit && resStatus === 'completed' && (oldPm === '포인트' || paymentMethod === '포인트');
        return (
            <>
                {/* 헤더 */}
                <div className="wf-header">
                    <div className="wf-title">
                        {isEdit ? '예약 수정' : '새 예약'}
                        {isEdit && resStatus && (
                            <span className={`wf-badge ${resStatus === 'completed' ? 'wf-b-done' : resStatus === 'in_progress' ? 'wf-b-active' : resStatus === 'noshow' ? 'wf-b-noshow' : resStatus === 'canceled' ? 'wf-b-cancel' : 'wf-b-pending'}`}>
                                {getStatusLabel(resStatus)}
                            </span>
                        )}
                    </div>
                    <button className="wf-close" onClick={resetToIdle}><FaTimes size={12} /></button>
                </div>

                {/* 수정 모드: 시간 배너 */}
                {isEdit && selectedReservation && (
                    <div className="wf-time-banner">
                        <div className="wf-tb-icon">{dayjs(formData.date).format('D')}</div>
                        <div className="wf-tb-info">
                            <div className="wf-tb-date">{dayjs(formData.date).format('M월 D일 (dd)')} {formData.time} ~ {dayjs(`${formData.date}T${formData.time}`).add(formData.duration || 30, 'minute').format('HH:mm')}</div>
                            <div className="wf-tb-detail">{formData.bayNumber}번 베이 · {(() => { const sf = staffList.find(s => s.id === parseInt(formData.staffId)); return sf ? (sf.owner_name || sf.username) : '미지정'; })()}</div>
                        </div>
                    </div>
                )}

                <div className="work-panel-body">
                    {error && <Alert variant="danger" className="py-1 small mx-3 mt-3 mb-0">{error}</Alert>}

                    {/* 일정 */}
                    <div className="wf-group">
                        <div className="wf-gt">일정{isEdit ? ' 변경' : ''}</div>
                        <div className="wf-sched-row">
                            <div className="wf-field wf-field-date">
                                <Form.Control type="date" size="sm" value={formData.date} onChange={e => setForm(prev => ({...prev, date: e.target.value}))} className="wf-input" />
                            </div>
                            <div className="wf-field">
                                <Form.Control type="time" size="sm" value={formData.time} onChange={e => setForm(prev => ({...prev, time: e.target.value}))} className="wf-input" />
                            </div>
                            <div className="wf-field wf-field-narrow">
                                <Form.Control type="number" size="sm" value={formData.duration}
                                    onChange={e => setForm(prev => ({...prev, duration: parseInt(e.target.value) || ''}))}
                                    onBlur={e => { const v = parseInt(e.target.value); setForm(prev => ({...prev, duration: Math.max(10, v || 10)})); }}
                                    className="wf-input" style={{ textAlign: 'center' }} />
                                <span className="wf-unit">분</span>
                            </div>
                        </div>
                        <div className="wf-sched-row2">
                            <Form.Select size="sm" value={formData.bayNumber} onChange={e => setForm(prev => ({...prev, bayNumber: parseInt(e.target.value)}))} className="wf-input">
                                {[...Array(bayCount)].map((_, i) => <option key={i+1} value={i+1}>{i+1}번 베이</option>)}
                            </Form.Select>
                            <Form.Select size="sm" value={formData.staffId} onChange={e => setForm(prev => ({...prev, staffId: e.target.value}))} className="wf-input">
                                <option value="">작업자 미지정</option>
                                {staffList.map(s => <option key={s.id} value={s.id}>{s.owner_name || s.username}</option>)}
                            </Form.Select>
                        </div>
                    </div>

                    {/* 서비스 */}
                    <div>
                        {showPointEditWarning && (
                            <div className="wf-group" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, padding: 8, fontSize: 12, color: '#1E40AF' }}>
                                포인트 결제건은 저장 시 자동으로 환불·재차감됩니다. 잔액이 부족하면 저장이 거절됩니다.
                            </div>
                        )}
                        <div className="wf-group">
                            <div className="wf-gt">서비스 메뉴</div>
                            <MenuTiles
                                menus={menus}
                                prices={prices}
                                sizeId={formData.temp_size_id}
                                selectedId={formData.service_menu_id}
                                onSelect={(id) => { setForm(prev => ({...prev, service_menu_id: id})); setMissingFields(prev => prev.filter(f => f !== 'service_menu_id')); }}
                                invalid={missingFields.includes('service_menu_id')}
                            />
                        </div>
                        {options.length > 0 && (
                            <div className="wf-group">
                                <div className="wf-gt">옵션</div>
                                <OptionTiles
                                    options={options}
                                    prices={prices}
                                    sizeId={formData.temp_size_id}
                                    selectedIds={formData.optionIds || []}
                                    onToggle={(id) => setForm(prev => ({
                                        ...prev,
                                        optionIds: (prev.optionIds || []).includes(id)
                                            ? (prev.optionIds || []).filter(x => x !== id)
                                            : [...(prev.optionIds || []), id]
                                    }))}
                                />
                            </div>
                        )}
                    </div>

                    {/* 메모 */}
                    <div className="wf-group">
                        <div className="wf-gt">메모</div>
                        <Form.Control as="textarea" rows={3} size="sm" placeholder="고객 요청사항, 특이사항 등..."
                            value={formData.memo} onChange={e => setForm(prev => ({...prev, memo: e.target.value}))}
                            className="wf-input" style={{ resize: 'none', lineHeight: '1.6' }} />
                    </div>

                    {/* 입고 정산 */}
                    {showPayment && (
                        <div className="wf-group wf-pay-group">
                            <div className="wf-gt">입고 정산</div>
                            <div className="wf-pay-box">
                                <Form.Select size="sm" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="wf-input" style={{ minWidth: 80, flex: 1 }}>
                                    <option value="카드">카드</option><option value="현금">현금</option>
                                    <option value="이체">이체</option><option value="포인트">포인트</option>
                                </Form.Select>
                                <AdjustmentInput
                                    adjustment={adjustment}
                                    placeholder="조정"
                                    inputClassName="wf-input"
                                    inputStyle={{ minWidth: 70, flex: 1 }}
                                />
                                <span className="wf-pay-total">{totalPrice > 0 ? `${totalPrice.toLocaleString()}원` : '-'}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* 액션 버튼 */}
                <div className="wf-footer">
                    <div className="d-flex gap-2">
                        {isEdit && (resStatus === 'pending' || resStatus === 'confirmed') && (
                            <>
                                <button className="wf-btn wf-btn-danger" onClick={handleCancelReservation} disabled={loading}>취소</button>
                                <button className="wf-btn wf-btn-outline" onClick={handleMarkNoshow} disabled={loading}>노쇼</button>
                            </>
                        )}
                        {isEdit && (resStatus === 'in_progress' || resStatus === 'completed') && (
                            <button className="wf-btn wf-btn-danger" onClick={handleCancelWork} disabled={loading}>작업취소</button>
                        )}
                    </div>
                    <div className="d-flex gap-2">
                        {(workMode === 'new' || (isEdit && (resStatus === 'pending' || resStatus === 'confirmed'))) && (
                            <button className="wf-btn wf-btn-success" onClick={handleStartWork} disabled={loading}>입고</button>
                        )}
                        {isEdit && resStatus === 'in_progress' && (
                            <button className="wf-btn wf-btn-success" onClick={handleComplete} disabled={loading}>완료</button>
                        )}
                        <button className="wf-btn wf-btn-primary" onClick={handleSubmit} disabled={loading}>
                            {loading ? '...' : isEdit ? '수정완료' : '저장'}
                        </button>
                    </div>
                </div>
            </>
        );
    };

    // ══════════════════════════════════════
    //  Render: CRM
    // ══════════════════════════════════════

    const renderCRM = () => {
        if (!customerHistory) return (
            <div className="text-center text-muted py-4 wb-text-sm" style={{ opacity: 0.4 }}>
                차량/고객 조회 시 이력 표시
            </div>
        );
        return (
            <div className="workboard-crm">
                {customerHistory.type === 'customer' && (
                    <div className="crm-split">
                        {/* 좌: 고객정보 + 보유차량 */}
                        <div className="crm-split-left">
                            <div className="crm-info-line">
                                <span className="crm-name">{customerHistory.data.customer.customerName}</span>
                                <span className="crm-phone">{customerHistory.data.customer.phoneNumber}</span>
                                <Badge bg="primary" pill className="crm-pts">{customerHistory.data.customer.points.toLocaleString()}P</Badge>
                                {customerHistory.data.customer.noshowCount > 0 && <Badge bg="danger" pill>노쇼 {customerHistory.data.customer.noshowCount}</Badge>}
                                {customerHistory.data.customer.canceledCount > 0 && <Badge bg="secondary" pill>취소 {customerHistory.data.customer.canceledCount}</Badge>}
                            </div>
                            {customerHistory.data.vehicles.length > 0 && (
                                <div className="crm-vehicle-list">
                                    {customerHistory.data.vehicles.map(v => (
                                        <div key={v.id} className="crm-vehicle-row" onClick={() => applyVehicleFromHistory(v)}>
                                            <strong className="text-primary">{v.plateNumber}</strong>
                                            <span className="text-muted">{v.manufacturer} {v.modelName}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* 우: 방문 내역 */}
                        <div className="crm-split-right">
                            {customerHistory.data.serviceHistory.length > 0 ? (
                                <>
                                    <div className="crm-history-title">방문 내역 ({customerHistory.data.serviceHistory.length})</div>
                                    {customerHistory.data.serviceHistory.slice(0, 5).map(h => (
                                        <div key={h.id} className="crm-visit-card">
                                            <div className="d-flex justify-content-between">
                                                <span className="fw-500">{h.menuName}</span>
                                                <span className="text-primary fw-bold">{parseFloat(h.finalPrice || 0).toLocaleString()}원</span>
                                            </div>
                                            {(h.optionsText || h.selectedOptions) && (
                                                <div className="crm-visit-opts" style={{ fontSize: '0.72rem', color: 'var(--bs-secondary)', marginTop: 2 }}>
                                                    + {h.optionsText || h.selectedOptions}
                                                </div>
                                            )}
                                            <div className="crm-visit-date">{dayjs(h.startTime || h.created_at).format('YYYY-MM-DD')} · {h.paymentMethod}</div>
                                        </div>
                                    ))}
                                </>
                            ) : <div className="text-muted wb-text-sm" style={{ opacity: 0.5 }}>방문 내역 없음</div>}
                        </div>
                    </div>
                )}
                {customerHistory.type === 'vehicle' && (
                    <div className="crm-split">
                        <div className="crm-split-left">
                            <div className="crm-info-line">
                                <span className="crm-name">{customerHistory.data.plateNumber}</span>
                                <Badge bg="secondary" pill>미등록</Badge>
                                {customerHistory.data.noshowCount > 0 && <Badge bg="danger" pill>노쇼 {customerHistory.data.noshowCount}</Badge>}
                                {customerHistory.data.canceledCount > 0 && <Badge bg="secondary" pill>취소 {customerHistory.data.canceledCount}</Badge>}
                            </div>
                        </div>
                        <div className="crm-split-right">
                            {customerHistory.data.serviceHistory.length > 0 ? (
                                <>
                                    <div className="crm-history-title">방문 내역 ({customerHistory.data.serviceHistory.length})</div>
                                    {customerHistory.data.serviceHistory.slice(0, 5).map(h => (
                                        <div key={h.id} className="crm-visit-card">
                                            <div className="d-flex justify-content-between">
                                                <span className="fw-500">{h.menuName}</span>
                                                <span className="text-primary fw-bold">{parseFloat(h.finalPrice || 0).toLocaleString()}원</span>
                                            </div>
                                            {(h.optionsText || h.selectedOptions) && (
                                                <div className="crm-visit-opts" style={{ fontSize: '0.72rem', color: 'var(--bs-secondary)', marginTop: 2 }}>
                                                    + {h.optionsText || h.selectedOptions}
                                                </div>
                                            )}
                                            <div className="crm-visit-date">{dayjs(h.startTime || h.created_at).format('YYYY-MM-DD')} · {h.paymentMethod}</div>
                                        </div>
                                    ))}
                                </>
                            ) : <div className="text-muted wb-text-sm" style={{ opacity: 0.5 }}>방문 내역 없음</div>}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ══════════════════════════════════════
    //  Main JSX
    // ══════════════════════════════════════

    return (
        <div className={`workboard-page ${panelLeft ? 'wb-panel-left' : ''}`}>
            {/* ===== LEFT: 검색+CRM + 달력 ===== */}
            <div className="wb-left">
            <div className="workboard-top">
                {topPanelOpen ? (
                    /* ── 펼침 상태: 좌(검색+정보) | 우(CRM) ── */
                    <div className="info-panel d-flex flex-column flex-lg-row">
                        {/* 좌측: 검색 + 정보 — 35% */}
                        <div className="wb-top-left">
                            {/* 검색 섹션 */}
                            <div className="wf-group">
                                <div className="wf-gt">차량 검색</div>
                                <div className="wb-form-row">
                                    <div className="position-relative flex-grow-1">
                                        <Form.Control size="sm" className="wf-input" placeholder="차량번호, 고객명, 연락처..."
                                            value={searchInput} onChange={handleSearchInput}
                                            onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()} />
                                        {suggestions.length > 0 && (
                                            <ListGroup className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000 }}>
                                                {suggestions.map(v => (
                                                    <ListGroup.Item key={v.id} action onClick={() => handleSelectFound(v)} className="small py-2">
                                                        <strong className="text-primary">{v.plateNumber}</strong>
                                                        <span className="ms-2 text-muted">{v.customerName || ''}</span>
                                                        <small className="ms-2 text-muted opacity-75">{v.phoneNumber}</small>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </div>
                                    <Button size="sm" variant="primary" onClick={handleSearchSubmit}>조회</Button>
                                </div>
                            </div>
                            {/* 차량 / 고객 섹션 */}
                            <div className="wf-group">
                                <div className="wf-gt">차량 / 고객</div>
                                <div className="wb-form-row mb-2">
                                    <div className="wb-field-wrap">
                                        <Form.Control size="sm" placeholder="차량번호" value={formData.plateNumber} readOnly={!!formData.vehicle_id}
                                            onChange={e => { handleFormChange('plateNumber', e.target.value); setMissingFields(prev => prev.filter(f => f !== 'plateNumber')); }}
                                            className={`wf-input${formData.vehicle_id ? ' field-locked' : ''}`} isInvalid={missingFields.includes('plateNumber')} />
                                        {formData.vehicle_id && <button className="wb-lock-btn" onClick={() => handleUnmap('vehicle')} title="차량 매핑 해제">✕</button>}
                                        {fieldSuggestions.field === 'plateNumber' && fieldSuggestions.items.length > 0 && (
                                            <ListGroup className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000, top: '100%' }}>
                                                {fieldSuggestions.items.map(v => (
                                                    <ListGroup.Item key={v.id} action onClick={() => handleSelectVehicleSuggestion(v)} className="small py-1">
                                                        <strong className="text-primary">{v.plateNumber}</strong> <span className="text-muted">{v.customerName || ''}</span>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </div>
                                    <div className="wb-field-wrap">
                                        <Form.Control size="sm" placeholder="고객명" value={formData.customerName} readOnly={!!formData.customer_id}
                                            onChange={e => handleFormChange('customerName', e.target.value)}
                                            className={`wf-input${formData.customer_id ? ' field-locked' : ''}`} />
                                        {formData.customer_id && <button className="wb-lock-btn" onClick={() => handleUnmap('customer')} title="고객 매핑 해제">✕</button>}
                                        {fieldSuggestions.field === 'customerName' && fieldSuggestions.items.length > 0 && (
                                            <ListGroup className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000, top: '100%' }}>
                                                {fieldSuggestions.items.map(c => (
                                                    <ListGroup.Item key={c.id} action onClick={() => handleSelectCustomerSuggestion(c)} className="small py-1">
                                                        <strong>{c.customerName}</strong> <span className="text-muted">{c.phoneNumber || ''}</span>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </div>
                                    <div className="wb-field-wrap">
                                        <Form.Control size="sm" placeholder={formData.customer_id && !linkedHasPhone ? '연락처 (이 고객에 저장)' : '연락처'}
                                            value={formData.phoneNumber}
                                            readOnly={!!formData.customer_id && linkedHasPhone}
                                            onChange={e => handleFormChange('phoneNumber', e.target.value)}
                                            className={`wf-input${(formData.customer_id && linkedHasPhone) ? ' field-locked' : ''}`} />
                                        {fieldSuggestions.field === 'phoneNumber' && fieldSuggestions.items.length > 0 && (
                                            <ListGroup className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000, top: '100%' }}>
                                                {fieldSuggestions.items.map(c => (
                                                    <ListGroup.Item key={c.id} action onClick={() => handleSelectCustomerSuggestion(c)} className="small py-1">
                                                        <strong>{c.customerName}</strong> <span className="text-muted">{c.phoneNumber || ''}</span>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </div>
                                </div>
                                <div className="wb-form-row">
                                    <Form.Select size="sm" className="wf-input" value={formData.temp_manufacturer} onChange={e => { handleManufacturerChange(e.target.value); setMissingFields(prev => prev.filter(f => f !== 'temp_manufacturer')); }} isInvalid={missingFields.includes('temp_manufacturer')}>
                                        <option value="">제조사{!settings?.is_car_model_required && ' (선택사항)'}</option>
                                        {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
                                    </Form.Select>
                                    <Form.Select size="sm" className="wf-input" value={formData.temp_model} onChange={e => { handleModelChange(e.target.value); setMissingFields(prev => prev.filter(f => f !== 'temp_model')); }} disabled={!formData.temp_manufacturer} isInvalid={missingFields.includes('temp_model')}>
                                        <option value="">모델{!settings?.is_car_model_required && ' (선택사항)'}</option>
                                        {filteredModels.map(m => <option key={m.id} value={m.modelName}>{m.modelName}</option>)}
                                    </Form.Select>
                                    <Form.Select size="sm" className="wf-input" value={formData.temp_size_id}
                                        onChange={e => { handleSizeChange(e.target.value); setMissingFields(prev => prev.filter(f => f !== 'temp_size_id')); }}
                                        isInvalid={missingFields.includes('temp_size_id')}>
                                        <option value="">사이즈</option>
                                        {vehicleSizes.map(s => <option key={s.id} value={s.id}>{s.sizeName}</option>)}
                                    </Form.Select>
                                </div>
                            </div>
                        </div>
                        {/* 우측: CRM — 65% */}
                        <div className="wb-top-right">
                            {renderCRM()}
                        </div>
                    </div>
                ) : (
                    /* ── 접힌 상태: 검색 바만 ── */
                    <div className="d-flex align-items-center gap-2 px-3 py-2">
                        <Button size="sm" variant="dark" disabled className="d-none d-md-inline">{currentDate.format('YYYY년 M월')}</Button>
                        <div className="position-relative flex-grow-1" style={{ maxWidth: 360 }}>
                            <Form.Control size="sm" placeholder="차량번호, 고객명, 연락처..."
                                value={searchInput} onChange={handleSearchInput}
                                onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
                                className="bg-light border-0" />
                            {suggestions.length > 0 && (
                                <ListGroup className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000 }}>
                                    {suggestions.map(v => (
                                        <ListGroup.Item key={v.id} action onClick={() => handleSelectFound(v)} className="small py-2">
                                            <strong className="text-primary">{v.plateNumber}</strong>
                                            <span className="ms-2 text-muted">{v.customerName || ''}</span>
                                            <small className="ms-2 text-muted opacity-75">{v.phoneNumber}</small>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </div>
                        <Button size="sm" variant="outline-primary" onClick={handleSearchSubmit}>조회</Button>
                    </div>
                )}
            </div>

            {/* Calendar */}
                <div className="workboard-calendar">
                    <div className="wb-cal-toolbar d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-light flex-shrink-0">
                        <ButtonGroup size="sm">
                            <Button variant={view === 'month' ? 'dark' : 'white'} onClick={() => updateNavigation('month')} className="border">월</Button>
                            <Button variant={view === 'week' ? 'dark' : 'white'} onClick={() => updateNavigation('week')} className="border">주</Button>
                            <Button variant={view === 'day' ? 'dark' : 'white'} onClick={() => updateNavigation('day')} className="border">일</Button>
                        </ButtonGroup>
                        <Button size="sm" variant="outline-secondary" onClick={() => updateNavigation(view, currentDate.subtract(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'))}>&lt;</Button>
                        <Button size="sm" variant="white" className="border fw-bold" onClick={() => updateNavigation(view, dayjs())}>오늘</Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => updateNavigation(view, currentDate.add(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'))}>&gt;</Button>
                        <span className="fw-bold small">{view === 'month' ? currentDate.format('YYYY년 M월') : currentDate.format('M월 D일 (ddd)')}</span>
                        <div className="ms-auto d-flex gap-1">
                            <Button size="sm" variant={showCanceled ? 'dark' : 'outline-secondary'} onClick={() => setShowCanceled(p => !p)} className="wb-text-sm">
                                {showCanceled ? '전체' : '취소숨김'}
                            </Button>
                            {/* 작업 패널 좌/우 전환. 세로로 쌓이는 모바일에서는 의미가 없어 CSS 로 숨긴다.
                                아이콘만 두면 뭔지 아무도 모른다 — 어디로 가는지 글자로 쓴다. */}
                            <Button size="sm" variant="outline-secondary" className="wb-side-toggle wb-text-sm"
                                onClick={togglePanelSide}
                                title={panelLeft ? '작업 패널을 오른쪽으로 옮깁니다' : '작업 패널을 왼쪽으로 옮깁니다'}>
                                <FaExchangeAlt size={11} className="me-1" />
                                {panelLeft ? '패널 오른쪽' : '패널 왼쪽'}
                            </Button>
                            {view === 'day' && (
                                <Button size="sm" variant={showSidebar ? 'dark' : 'outline-secondary'} onClick={() => setShowSidebar(p => !p)}><FaList size={12} /></Button>
                            )}
                        </div>
                    </div>

                    <div className="flex-grow-1 d-flex" style={{ minHeight: 0 }}>
                        {view === 'month' ? renderMonthView() : (
                            <>
                                <div className="custom-scheduler-wrapper d-flex flex-column flex-grow-1 wb-min-w-0">
                                    <div className="scheduler-header d-flex border-bottom bg-light sticky-top" style={{ zIndex: 30 }}>
                                        <div className="time-col-header border-end d-flex align-items-end justify-content-center pb-2 text-muted small">시간</div>
                                        <div className="days-container flex-grow-1 d-flex">
                                            {visibleDays.map((day, i) => (
                                                <div key={i} className="day-col flex-grow-1 border-end">
                                                    <div className={`text-center py-2 fw-bold ${day.isSame(dayjs(), 'day') ? 'bg-primary text-white' : 'text-dark'}`}>
                                                        {day.format('M/D')} ({['일','월','화','수','목','금','토'][day.day()]})
                                                    </div>
                                                    <div className="d-flex border-top bg-white">
                                                        {Array.from({ length: bayCount }).map((_, b) => (
                                                            <div key={b} className="bay-title flex-grow-1 text-center py-1 text-muted border-end opacity-75 wb-text-sm">{b + 1}번</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="scheduler-body d-flex position-relative overflow-y-auto flex-grow-1" ref={scrollContainerRef}>
                                        <div className="time-col border-end bg-white">
                                            {Array.from({ length: TOTAL_HOURS + 1 }).map((_, i) => (
                                                <div key={i} className="time-label text-muted small text-end pe-2" style={{ height: PIXELS_PER_HOUR }}>{`${START_HOUR + i}:00`}</div>
                                            ))}
                                        </div>
                                        <div className="grid-container flex-grow-1 position-relative" ref={gridRef} onMouseDown={handleGridMouseDown} style={{ height: TOTAL_HOURS * PIXELS_PER_HOUR }}>
                                            {Array.from({ length: TOTAL_HOURS }).map((_, i) => (
                                                <React.Fragment key={i}>
                                                    <div className="grid-h-line w-100 position-absolute" style={{ top: i * PIXELS_PER_HOUR }} />
                                                    <div className="grid-h-line-half w-100 position-absolute" style={{ top: (i * PIXELS_PER_HOUR) + (PIXELS_PER_HOUR / 2) }} />
                                                </React.Fragment>
                                            ))}
                                            {filteredEvents.map(ev => {
                                                const style = getEventStyles(ev);
                                                if (style.display === 'none') return null;
                                                const isSel = selectedReservation?.id === ev.id;
                                                const showEdit = isSel && workMode === 'edit' && formData.time;
                                                const startStr = showEdit ? formData.time : dayjs(ev.startTime).format('HH:mm');
                                                const endStr = showEdit
                                                    ? dayjs(formData.date + ' ' + formData.time).add(formData.duration || 30, 'minute').format('HH:mm')
                                                    : dayjs(ev.endTime).format('HH:mm');
                                                const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
                                                return (
                                                    <div key={ev.id} onMouseDown={(e) => handleActionMouseDown(e, ev)}
                                                        className={`event-card ${isSel ? 'selected-event' : 'overflow-hidden'}`}
                                                        style={{ ...style, backgroundColor: getStatusAccent(ev.status), color: '#fff', cursor: activeAction ? 'grabbing' : 'grab', opacity: activeEventId === ev.id ? 0.8 : 1 }}
                                                        onClick={() => { if (!isMovingRef.current) handleEventClick(ev); }}>
                                                        {isSel && <div className="ev-time-label ev-time-start"
                                                            onMouseDown={(e) => handleTimeLabelDrag(e, ev, 'start')}
                                                            onTouchStart={(e) => handleTimeLabelDrag(e, ev, 'start')}
                                                        >{startStr}</div>}
                                                        {(() => {
                                                            const sectionDivider = { borderTop: '1px solid rgba(255,255,255,0.25)', marginTop: 3, paddingTop: 3 };
                                                            const hasVehicle = ev.plateNumber || ev.manufacturer || ev.modelName;
                                                            const hasService = ev.menuName || ev.optionsText;
                                                            const optionLines = ev.optionsText
                                                                ? ev.optionsText.split(/[,/]/).map(o => o.trim()).filter(Boolean)
                                                                : [];
                                                            return (
                                                                <>
                                                                    {/* 시간 */}
                                                                    <div className="ev-time" style={{ lineHeight: '1.1', fontSize: '0.65rem', opacity: 0.9 }}>
                                                                        <div>{startStr}</div>
                                                                        <div>~ {endStr}</div>
                                                                    </div>
                                                                    {/* 차량 정보 */}
                                                                    {hasVehicle && (
                                                                        <div style={sectionDivider}>
                                                                            {ev.plateNumber && <div className="ev-plate" style={{ ...ellipsis, fontWeight: 600 }}>{ev.plateNumber}</div>}
                                                                            {(ev.manufacturer || ev.modelName) && (
                                                                                <div className="ev-model" style={{ ...ellipsis, fontSize: '0.7rem', opacity: 0.85 }}>
                                                                                    {[ev.manufacturer, ev.modelName].filter(Boolean).join(' ')}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {/* 서비스 정보 */}
                                                                    {hasService && (
                                                                        <div style={sectionDivider}>
                                                                            {ev.menuName && <div className="ev-menu" style={{ ...ellipsis, fontSize: '0.7rem', opacity: 0.95 }}>{ev.menuName}</div>}
                                                                            {optionLines.map((opt, i) => (
                                                                                <div key={i} className="ev-option" style={{ ...ellipsis, fontSize: '0.65rem', opacity: 0.8 }}>{opt}</div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {/* 담당자 */}
                                                                    {ev.staffName && (
                                                                        <div style={sectionDivider}>
                                                                            <div className="ev-staff" style={{ ...ellipsis, fontSize: '0.65rem', opacity: 0.85 }}>👤 {ev.staffName}</div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                        {isSel && <div className="ev-time-label ev-time-end"
                                                            onMouseDown={(e) => handleTimeLabelDrag(e, ev, 'end')}
                                                            onTouchStart={(e) => handleTimeLabelDrag(e, ev, 'end')}
                                                        >{endStr}</div>}
                                                    </div>
                                                );
                                            })}
                                            {/* Drag preview (드래그 중) */}
                                            {newPreview && (() => {
                                                const dayIndex = visibleDays.findIndex(d => d.isSame(newPreview.start, 'day'));
                                                if (dayIndex === -1) return null;
                                                const top = (newPreview.start.diff(newPreview.start.startOf('day').hour(START_HOUR), 'minute') / 60) * PIXELS_PER_HOUR;
                                                const height = Math.max(15, (newPreview.end.diff(newPreview.start, 'minute') / 60) * PIXELS_PER_HOUR);
                                                const wp = 100 / (visibleDays.length * bayCount);
                                                const lp = ((dayIndex * bayCount) + (newPreview.bay - 1)) * wp;
                                                return (
                                                    <div className="new-reservation-preview dragging rounded-2"
                                                        style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left: `${lp}%`, width: `${wp}%`, zIndex: 50 }}>
                                                        <div className="px-2 py-1 small fw-bold text-primary">
                                                            {newPreview.start.format('HH:mm')} - {newPreview.end.format('HH:mm')}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {/* Form-based preview (폼에서 시간 설정 시 실시간 표시) */}
                                            {!newPreview && (workMode === 'new' || (workMode === 'edit' && selectedReservation)) && formData.date && formData.time && (() => {
                                                const fStart = dayjs(`${formData.date} ${formData.time}`);
                                                if (!fStart.isValid()) return null;
                                                const fEnd = fStart.add(formData.duration || 30, 'minute');
                                                const fBay = formData.bay || formData.bayNumber || 1;
                                                const dayIndex = visibleDays.findIndex(d => d.isSame(fStart, 'day'));
                                                if (dayIndex === -1) return null;
                                                // edit 모드에서는 실제 이벤트 블록이 이미 formData 반영하므로 스킵
                                                if (workMode === 'edit') return null;
                                                const top = (fStart.diff(fStart.startOf('day').hour(START_HOUR), 'minute') / 60) * PIXELS_PER_HOUR;
                                                const height = Math.max(15, (fEnd.diff(fStart, 'minute') / 60) * PIXELS_PER_HOUR);
                                                const wp = 100 / (visibleDays.length * bayCount);
                                                const lp = ((dayIndex * bayCount) + (fBay - 1)) * wp;
                                                return (
                                                    <div className="new-reservation-preview rounded-2"
                                                        style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left: `${lp}%`, width: `${wp}%`, zIndex: 50 }}>
                                                        <div className="ev-time-label ev-time-start"
                                                            onMouseDown={(e) => handleFormPreviewResize(e, 'start')}
                                                            onTouchStart={(e) => handleFormPreviewResize(e, 'start')}
                                                        >{fStart.format('HH:mm')}</div>
                                                        <div className="ev-time-label ev-time-end"
                                                            onMouseDown={(e) => handleFormPreviewResize(e, 'end')}
                                                            onTouchStart={(e) => handleFormPreviewResize(e, 'end')}
                                                        >{fEnd.format('HH:mm')}</div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                {view === 'day' && showSidebar && (
                                    <div className="schedule-sidebar border-start bg-white d-flex flex-column" style={{ width: 240, minWidth: 240 }}>
                                        <div className="px-3 py-2 bg-light border-bottom"><span className="fw-bold small text-dark">일정 목록</span></div>
                                        <div className="flex-grow-1 overflow-y-auto">
                                            {visibleDays.map(day => {
                                                const de = filteredEvents.filter(ev => dayjs(ev.startTime).isSame(day, 'day')).sort((a,b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
                                                if (de.length === 0) return null;
                                                return (
                                                    <div key={day.format('YYYY-MM-DD')}>
                                                        {de.map(ev => (
                                                            <div key={ev.id}
                                                                className={`sidebar-event-item d-flex align-items-start px-3 py-2 border-bottom ${selectedReservation?.id === ev.id ? 'active' : ''}`}
                                                                style={{ cursor: 'pointer' }} onClick={() => handleEventClick(ev)}>
                                                                <div className="idle-time-block" style={{ minWidth: 44 }}>
                                                                    <div className="idle-time-num">{dayjs(ev.startTime).format('HH:mm')}</div>
                                                                    <div className="idle-time-bay">{ev.bayNumber}번</div>
                                                                </div>
                                                                <div className="flex-grow-1 wb-min-w-0">
                                                                    <div className="fw-bold small text-truncate">{ev.customerName || ev.plateNumber || '-'}</div>
                                                                    <div className="text-muted text-truncate wb-text-sm">{ev.serviceName || '메뉴 미지정'} · {ev.staffName || '미정'}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })}
                                            {filteredEvents.length === 0 && <div className="text-center text-muted py-5 small">예약이 없습니다.</div>}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

            </div>{/* end wb-left */}

            {/* ===== RIGHT: 오늘의 예약 / 예약 수정 (sticky) ===== */}
            <div className="wb-right">
                <div className="workboard-work-panel">
                    {workMode === 'idle' ? renderIdlePanel() : renderWorkForm()}
                </div>
            </div>

            {/* Day detail modal (month view) */}
            <Modal show={!!dayDetailDate} onHide={() => setDayDetailDate(null)} centered size="md">
                <Modal.Header closeButton className="bg-light border-bottom-0 pb-2">
                    <Modal.Title className="fs-6 fw-bold">{dayDetailDate && dayDetailDate.format('M월 D일 (ddd)')} 예약 현황</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {dayDetailDate && (() => {
                        const de = filteredEvents.filter(ev => dayjs(ev.startTime).isSame(dayDetailDate, 'day')).sort((a,b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
                        if (de.length === 0) return <div className="text-center text-muted py-5">예약이 없습니다.</div>;
                        return de.map(ev => (
                            <div key={ev.id} className="day-detail-item d-flex align-items-center px-3 py-2 border-bottom"
                                style={{ cursor: 'pointer' }} onClick={() => { setDayDetailDate(null); handleEventClick(ev); }}>
                                <span className="status-tag me-3" style={{ backgroundColor: getStatusColor(ev.status) }}>{getStatusLabel(ev.status)}</span>
                                <div className="day-detail-time text-muted me-3" style={{ minWidth: 90, fontSize: '0.85rem' }}>{dayjs(ev.startTime).format('HH:mm')} - {dayjs(ev.endTime).format('HH:mm')}</div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold small">{ev.customerName || ev.plateNumber || '-'}</div>
                                    <div className="text-muted wb-text-sm">{ev.serviceName || '메뉴 미지정'} · {ev.staffName || '미정'}</div>
                                </div>
                                <Badge bg="light" text="dark" className="border ms-2 wb-text-sm">{ev.bayNumber}번</Badge>
                            </div>
                        ));
                    })()}
                </Modal.Body>
                <Modal.Footer className="border-top-0 pt-0 justify-content-between">
                    <Button variant="outline-primary" size="sm" onClick={() => { const d = dayDetailDate; setDayDetailDate(null); handleNewFromDate(d); }}>
                        <FaPlus size={10} className="me-1" /> 예약 추가
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => { const d = dayDetailDate; setDayDetailDate(null); updateNavigation('day', d); }}>
                        <FaArrowRight size={10} className="me-1" /> 일간 보기
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default WorkboardPage;
